import type { EntityKind } from '@/api/base-types';
import { useJournal } from '@/api/base';
import { Button } from '@/components/kit/Button';
import { Modal } from '@/components/kit/Modal';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/kit/Note';

const when = (iso: string) => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * «Журнал изменений» карточки: кто, что, когда и какие поля изменились
 * (БЛ M2 §10). Подписи действий и значения приходят с сервера.
 */
export function JournalModal({ entity, id, onClose }: { entity: EntityKind; id: string; onClose: () => void }) {
  const journal = useJournal(entity, id);
  const items = journal.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Modal title="Журнал изменений" size="xl" onClose={onClose}>
      {journal.isLoading ? (
        <TableSkeleton rows={5} cols={3} />
      ) : journal.isError ? (
        <ErrorState message={journal.error.message} onRetry={() => void journal.refetch()} />
      ) : !items.length ? (
        <EmptyState>Записей пока нет.</EmptyState>
      ) : (
        <table className="tbl mtbl">
          <thead>
            <tr>
              <th>Когда</th>
              <th>Кто</th>
              <th>Действие</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id}>
                <td className="num nw">{when(i.at)}</td>
                <td>{i.user ?? <span className="dim">система</span>}</td>
                <td>
                  {i.action}
                  {i.changes.map((c) => (
                    <div className="sub" key={c.label}>
                      {c.label}: {c.from ?? '—'} → {c.to ?? '—'}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {journal.hasNextPage ? (
        <Button small tone="ghost" onClick={() => void journal.fetchNextPage()}>
          Показать ещё
        </Button>
      ) : null}
    </Modal>
  );
}
